"use client";

import Image from "next/image";
import { ArticleCard } from "@/components/blog/article-card";
import { TableOfContents } from "@/components/blog/table-of-contents";
import { PublicLink as Link } from "@/components/public/PublicLink";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useTableOfContents } from "@/hooks/useTableOfContents";
import { getBlogPostImage } from "@/lib/blog-images";
import { ArrowLeft, Calendar, Clock, Share2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { reportClientError } from "@/lib/observability/report-client-error";
import { formatDate as safeFormatDate } from "@/lib/utils/format";
import { trackEvent } from "@/lib/analytics/amplitude";
import { SHARE_CLICKED } from "@/lib/analytics/amplitude-events";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";

interface RelatedPost {
  slug: string;
  title: string;
  description: string;
  publishedAt: string;
  readTime: string;
  tags: string[];
  lang: string;
}

interface BlogPostClientProps {
  slug: string;
  locale: string;
  title: string;
  description: string;
  publishedAt: string;
  updatedAt?: string;
  readTime: string;
  tags: string[];
  content: string;
  relatedPosts: RelatedPost[];
  translations: {
    backToBlog: string;
    authorName: string;
    authorLabel: string;
    updatedLabel: string;
    share: string;
    copied: string;
    relatedTitle: string;
    ctaTitle: string;
    ctaDescription: string;
    ctaButton: string;
  };
}

function formatDate(dateString: string, locale: string): string {
  try {
    return safeFormatDate(dateString, dateString, locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (error) {
    reportClientError(error, {
      source: "components.BlogPostClient",
      feature: "blog",
      action: "formatDate",
    });
    return dateString;
  }
}

export function BlogPostClient({
  slug,
  locale,
  title,
  description,
  publishedAt,
  updatedAt,
  readTime,
  tags,
  content,
  relatedPosts,
  translations: t,
}: BlogPostClientProps) {
  const [copied, setCopied] = useState(false);
  const proseRef = useRef<HTMLDivElement>(null);
  const { items: tocItems, activeId: tocActiveId } = useTableOfContents(
    proseRef,
    content
  );

  const handleShare = useCallback(async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      await navigator.clipboard.writeText(url);
      trackEvent(SHARE_CLICKED, { page: "blog", url });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      reportClientError(error, {
        source: "components.BlogPostClient",
        feature: "blog",
        action: "handleShare",
      });
      /* ignore */
    }
  }, []);

  const heroImage = getBlogPostImage(slug);

  return (
    <div className="container mx-auto px-4 py-12 pt-24 sm:py-16">
      <div className="mx-auto max-w-4xl">
        <Link
          href="/blog"
          className="hover:text-foreground text-muted-foreground/70 mb-8 inline-flex items-center gap-2 text-sm font-medium transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t.backToBlog}
        </Link>

        <article>
          {heroImage && (
            <div className="border-border/50 relative mb-8 aspect-[2/1] overflow-hidden rounded-xl border">
              <Image
                src={heroImage}
                alt={title}
                fill
                className="object-cover"
                unoptimized
                priority
              />
            </div>
          )}

          <header className="mb-8">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {tags?.map((tag) => (
                <span
                  key={tag}
                  className="text-leaf-600 dark:text-leaf-400/80 text-[11px] font-semibold tracking-widest uppercase"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-foreground mb-4 text-3xl leading-tight font-extrabold sm:text-4xl lg:text-5xl">
              {title}
            </h1>
            <p className="text-muted-foreground mb-6 text-lg">{description}</p>
            <div className="text-muted-foreground/70 flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="bg-leaf-500/20 text-leaf-600 dark:text-leaf-400 flex h-8 w-8 items-center justify-center rounded-full font-semibold">
                  V
                </div>
                <div>
                  <p className="text-foreground font-medium">{t.authorName}</p>
                  <p className="text-xs">{t.authorLabel}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(publishedAt, locale)}
              </div>
              {updatedAt && (
                <div className="flex items-center gap-1">
                  <span className="font-medium">{t.updatedLabel}</span>
                  {formatDate(updatedAt, locale)}
                </div>
              )}
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {readTime}
              </div>
              <button
                type="button"
                onClick={handleShare}
                className="hover:text-foreground text-muted-foreground ml-auto flex items-center gap-2 transition-colors"
              >
                <Share2 className="h-4 w-4" />
                {copied ? t.copied : t.share}
              </button>
            </div>
          </header>

          <div className="flex flex-col gap-12 lg:flex-row">
            <div ref={proseRef} className="min-w-0 flex-1">
              <div className="blog-prose">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, rehypeSanitize, rehypeHighlight]}
                >
                  {content}
                </ReactMarkdown>
              </div>
            </div>

            {tocItems.length > 0 && (
              <aside className="hidden w-56 flex-shrink-0 lg:block">
                <div className="sticky top-24">
                  <TableOfContents items={tocItems} activeId={tocActiveId} />
                </div>
              </aside>
            )}
          </div>

          {relatedPosts.length > 0 && (
            <section className="border-border mt-16 border-t pt-12">
              <h2 className="text-foreground mb-6 text-xl font-bold">
                {t.relatedTitle}
              </h2>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {relatedPosts.map((rp) => (
                  <ArticleCard
                    key={`${rp.slug}-${rp.lang}`}
                    slug={rp.slug}
                    title={rp.title}
                    description={rp.description}
                    date={rp.publishedAt}
                    readTime={rp.readTime}
                    tags={rp.tags}
                    variant="compact"
                  />
                ))}
              </div>
            </section>
          )}

          <Card
            variant="glass"
            className="from-leaf-500/10 to-leaf-500/10 mt-12 bg-gradient-to-r"
          >
            <CardContent className="pt-6 text-center">
              <h3 className="text-foreground mb-2 text-xl font-semibold">
                {t.ctaTitle}
              </h3>
              <p className="text-muted-foreground mb-4">{t.ctaDescription}</p>
              <Link href="/getting-started">
                <Button className="bg-leaf-500 hover:bg-leaf-600">
                  {t.ctaButton}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </article>
      </div>
    </div>
  );
}
